import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import type { WalletsMeta } from '../models/Wallets';
import { WalletsApiAdapter, WalletsFilters } from '../adapters/WalletsApiAdapter';

const DEFAULT_FILTERS: WalletsFilters = {};

/** La API puede devolver meta en snake_case o camelCase */
type ApiMeta = Partial<WalletsMeta> & {
  per_page?: number;
  current_page?: number;
  last_page?: number;
};

function normalizeWalletsMeta(meta: ApiMeta): {
  total: number;
  currentPage: number;
  lastPage: number;
} {
  return {
    total: meta.total ?? 0,
    currentPage: meta.currentPage ?? meta.current_page ?? 1,
    lastPage: meta.lastPage ?? meta.last_page ?? 1,
  };
}

export const useWalletsInfinite = (filters?: WalletsFilters) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(() => new WalletsApiAdapter(httpClient), [httpClient]);
  const resolvedFilters = filters ?? DEFAULT_FILTERS;

  return useInfiniteQuery({
    queryKey: ['wallets', 'infinite', resolvedFilters],
    queryFn: async ({ pageParam }) => {
      const response = await apiAdapter.getWallets({
        ...resolvedFilters,
        page: pageParam as number,
        perPage: 25,
      });
      return {
        data: response.data,
        meta: normalizeWalletsMeta(response.meta as ApiMeta),
      };
    },
    getNextPageParam: (lastPage) => {
      const { currentPage, lastPage: last } = lastPage.meta;
      if (currentPage < last) {
        return currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 60 * 1000,
  });
};
